import { emailRegex, scrollToElement } from '@components/utils';
import PreCheck from '@components/components/HeatInstallationForm/components/PreCheck';
import Detail from '@components/components/HeatInstallationForm/components/Detail';
import Result from '@components/components/HeatInstallationForm/components/Result';
import Contact from '@components/components/HeatInstallationForm/components/Contact';
import Failed from '@components/components/HeatInstallationForm/components/Failed';
import Success from '@components/components/HeatInstallationForm/components/Success';
import styles from '@components/components/HeatInstallationForm/HeatInstallationForm.module.scss';
import { useAjax } from '@components/hooks';
import { useLocalStorage, useMedia, useTracking } from '@components/hooks';
import React, { useState, useEffect, useRef } from 'react';
import { useFormStore, Form, FormInput } from '@ariakit/react';

const steps = [
  {
    Component: PreCheck,
    name: 'fernwaerme_nachinstallation_start',
    order: 1,
    required: {
      isOwner: 'HauptmieterIn und/oder WohnungseigentümerIn',
    },
  },
  {
    Component: Detail,
    name: 'fernwaerme_nachinstallation_wohnungsangabe',
    order: 2,
    required: {
      size: 'Wohnungsgröße in m2',
      radiatorCount: 'Anzahl der Heizkörper',
      warmWaterDevice: 'Interesse an Warmwassergerät',
    },
  },
  {
    Component: Result,
    name: 'fernwaerme_nachinstallation_kostenkalkulation',
    order: 3,
    condition: store => store.getValue('size') > 21,
  },
  {
    Component: Contact,
    name: 'fernwaerme_nachinstallation_kontaktformular',
    order: 4,
    isFinalStep: true,
    required: {
      vorname: 'Vorname',
      nachname: 'Nachname',
      email: 'E-Mail',
      strasse: 'Straße',
      gebadr1: 'Hausnummer',
      plz: 'PLZ',
      ort: 'Ort',
    },
    validation: store => {
      const email = store.getValue('email');

      if (!emailRegex.test(email.trim().toLowerCase())) {
        store.setError('email', 'E-Mail-Adresse muss gültig sein');
      }
    },
  },
  {
    Component: Success,
    name: 'fernwaerme_nachinstallation_danke',
    order: 5,
  },
  {
    Component: Failed,
    name: 'fernwaerme_nachinstallation_failed',
    order: 6,
  },
];

const findPrevValidStep = (startAt, store) =>
  steps.reduce(
    (accu, item) =>
      (!item.condition || item.condition(store)) &&
      item.order > accu &&
      item.order < startAt
        ? item.order
        : accu,
    1,
  );

const findNextValidStep = (startAt, store) =>
  steps.reduce(
    (accu, item) =>
      (!item.condition || item.condition(store)) &&
      item.order < accu &&
      item.order > startAt
        ? item.order
        : accu,
    steps.length,
  );

const HeatInstallationForm = ({
  blockId,
  addressPickerLink,
  planningPrice,
  basePrice,
  floorspacePrice,
  radiatorPrice,
  waterDevicePrice,
  handleNextStep: handleGeneralNextStep,
  handlePreviousStep,
  isGeneralRecommender,
  handleSkipStep: handleGeneralSkipStep,
}) => {
  const [step, setStep] = useState(1);
  const track = useTracking();
  const [address] = useLocalStorage('address-check');
  const scrollRef = useRef();
  const isMobile = useMedia('(max-width: 767px)');
  const [submitData] = useAjax(
    'we_theme_heat_installation_controller_send_order',
  );

  const strasse = address?.strasse || '';
  const gebadr1 = address?.gebadr1 || '';
  const tuerNr = address?.tuerNr || '';
  const plzOrt = address?.plzOrt || '';
  const plz = plzOrt?.split(' ')?.[0] || '';
  const ort = plzOrt?.split(' ')?.[1] || '';

  const [values, setValues] = useState({
    isOwner: null,
    size: '',
    radiatorCount: '',
    warmWaterDevice: 'Nein',
    totalPrice: null,
    anrede: 'Frau',
    vorname: '',
    nachname: '',
    telefonnummer: '',
    email: '',
    planningPrice,
    basePrice,
    radiatorPrice,
    floorspacePrice,
    waterDevicePrice,
    strasse,
    gebadr1,
    tuerNr,
    plz,
    ort,
    addressPickerLink,
  });

  const form = useFormStore({ values, setValues });

  form.useValidate(() => {
    const currentStep = steps.find(item => item.order === step);

    if (currentStep.order === 2 && values.size < 1) {
      form.setError(
        'size',
        `${currentStep.required.size} muss größer als 0 sein`,
      );
    }

    if (currentStep.order === 2 && values.radiatorCount < 1) {
      form.setError(
        'radiatorCount',
        `${currentStep.required.radiatorCount} muss größer als 0 sein`,
      );
    }

    if (currentStep.required) {
      Object.keys(currentStep.required).forEach(key => {
        if (!values[key]) {
          form.setError(key, `${currentStep.required[key]} ist erforderlich`);
        }
      });
    }

    if (currentStep.validation) {
      currentStep.validation(form);
    }
  });

  const setNextValidStep = () => {
    setStep(findNextValidStep(step, form));
    scrollToElement(scrollRef.current, isMobile);
  };

  const setPrevValidStep = () => {
    setStep(findPrevValidStep(step, form));
    scrollToElement(scrollRef.current, isMobile);
  };

  form.useSubmit(async () => {
    try {
      const formData = new FormData();

      for (const key in values) {
        if (Object.hasOwnProperty.call(values, key)) {
          const value = values[key];
          formData.append(key, value);
        }
      }
      formData.append('blockId', blockId);

      await submitData(formData);

      track({
        event: 'heatinstallation_abgeschlossen',
        title: 'heatinstallation order overview',
        options: {
          isOwner: formData.isOwner,
          size: formData.size,
          radiatorCount: formData.radiatorCount,
          warmWaterDevice: formData.warmWaterDevice,
        },
      });

      if (isGeneralRecommender) {
        handleGeneralNextStep(values);
      } else {
        // Success Step
        setStep(5);
      }
    } catch (error) {
      // Error Step
      setStep(6);
    } finally {
      // scroll to top of component
      scrollToElement(scrollRef.current, isMobile);
    }
  });

  useEffect(() => {
    const totalPrice =
      planningPrice +
      basePrice +
      floorspacePrice * values.size +
      values.radiatorCount * values.radiatorPrice +
      (values.warmWaterDevice === 'Ja' ? values.waterDevicePrice : 0);

    form.setValue('totalPrice', totalPrice);
  }, [values.radiatorCount, values.warmWaterDevice]);

  const renderStep = () => {
    const CurrentStepComponent = steps.filter(item => item.order === step)[0];

    return (
      <CurrentStepComponent.Component
        className={styles.form}
        data-purpose="heat-installation-form"
        handleGeneralNextStep={() => handleGeneralNextStep(values)}
        handleGeneralPrevStep={handlePreviousStep}
        handleSkipStep={handleGeneralSkipStep}
        isFinalStep={CurrentStepComponent.isFinalStep}
        isGeneralRecommender={isGeneralRecommender}
        prev={setPrevValidStep}
        resetOnSubmit={false}
        setNextValidStep={setNextValidStep}
        setStep={setStep}
        step={step}
        store={form}
      />
    );
  };

  return (
    <Form
      className={styles.form}
      data-purpose="heat-installation-form"
      ref={scrollRef}
      store={form}
    >
      <FormInput name="totalPrice" store={form} type="hidden" />
      {renderStep()}
    </Form>
  );
};

export default HeatInstallationForm;
